import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { UpgradeInfo } from '../../types';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background-color: var(--color-surface-2);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: var(--spacing-8);
  max-width: 460px;
  width: 90%;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const IconWrapper = styled.div<{ $variant: 'lock' | 'limit' | 'warning' }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.$variant) {
      case 'limit': return 'rgba(255, 138, 92, 0.1)';
      case 'lock': return 'rgba(0, 218, 109, 0.1)';
      default: return 'rgba(249, 115, 22, 0.1)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-4);
  color: ${props => {
    switch (props.$variant) {
      case 'limit': return 'var(--color-report)';
      case 'lock': return 'var(--color-primary)';
      default: return 'var(--color-warning)';
    }
  }};
  font-size: 24px;
`;

const Title = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-2);
`;

const Description = styled.p`
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-4);
  line-height: 1.6;
`;

const UsageBar = styled.div`
  background: var(--color-surface);
  border-radius: var(--radius-md);
  padding: var(--spacing-4);
  margin-bottom: var(--spacing-4);
`;

const UsageBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
`;

const UsageBarTrack = styled.div`
  height: 8px;
  background: var(--color-line);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: var(--spacing-2);
`;

const UsageBarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => Math.min(props.$percent, 100)}%;
  background: ${props => props.$percent >= 100 ? 'var(--color-error)' : props.$percent >= 80 ? 'var(--color-report)' : 'var(--color-primary)'};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ResetInfo = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
`;

const TierInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-4);
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-6);
  font-size: var(--font-size-sm);
`;

const TierBadge = styled.span<{ $type: 'current' | 'required' }>`
  display: inline-block;
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  background-color: ${props => props.$type === 'required' ? 'rgba(0, 218, 109, 0.1)' : 'var(--color-surface)'};
  color: ${props => props.$type === 'required' ? 'var(--color-primary)' : 'var(--color-text-secondary)'};
`;

const Arrow = styled.i`
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-3);
`;


const SecondaryButton = styled.button`
  flex: 1;
  padding: var(--spacing-3) var(--spacing-4);
  background-color: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-line);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background-color: var(--color-surface);
  }
`;

interface UpgradeModalProps {
  upgradeInfo: UpgradeInfo;
  onClose: () => void;
}

const formatTierName = (tier: string): string => {
  if (!tier) return 'Free';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};

const formatResetDate = (resetDate: string | null | undefined): string => {
  if (!resetDate) return '';
  const date = new Date(resetDate);
  return date.toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' });
};

// Backend uses year 2099 as a sentinel for "Total" (lifetime, never resets)
// limit periods. Treat any such date — and any unparseable date — as
// "no real reset" so the modal can hide the misleading "Resets on …" line.
const isLifetimeSentinel = (resetDate: string | null | undefined): boolean => {
  if (!resetDate) return true;
  const d = new Date(resetDate);
  return Number.isNaN(d.getTime()) || d.getUTCFullYear() >= 2099;
};

const UpgradeModal: React.FC<UpgradeModalProps> = ({ upgradeInfo, onClose }) => {
  const navigate = useNavigate();
  const infoType = upgradeInfo.type || 'subscription_required';

  const handleViewPlans = () => {
    onClose();
    navigate('/pricing');
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getIcon = () => {
    switch (infoType) {
      case 'usage_limit_reached': return 'fa-chart-bar';
      case 'feature_not_available': return 'fa-lock';
      default: return 'fa-lock';
    }
  };

  const getVariant = (): 'lock' | 'limit' | 'warning' => {
    switch (infoType) {
      case 'usage_limit_reached': return 'limit';
      case 'feature_not_available': return 'lock';
      default: return 'warning';
    }
  };

  const getTitle = () => {
    switch (infoType) {
      case 'usage_limit_reached': return 'Usage Limit Reached';
      case 'feature_not_available': return 'Feature Not Available';
      default: return 'Upgrade Required';
    }
  };

  const getDescription = () => {
    if (upgradeInfo.message) return upgradeInfo.message;

    switch (infoType) {
      case 'usage_limit_reached':
        return `You've used all ${upgradeInfo.limit} ${upgradeInfo.featureDisplayName || 'allowed actions'} this period. Upgrade to ${formatTierName(upgradeInfo.requiredTier)} for higher limits.`;
      case 'feature_not_available':
        return `${upgradeInfo.featureDisplayName || 'This feature'} is available on the ${formatTierName(upgradeInfo.requiredTier)} plan and above. Upgrade to unlock it.`;
      default:
        return `This feature requires a ${formatTierName(upgradeInfo.requiredTier)} plan or higher. Upgrade your subscription to access this functionality.`;
    }
  };

  const usagePercent = upgradeInfo.limit && upgradeInfo.limit > 0
    ? Math.round(((upgradeInfo.currentUsage || 0) / upgradeInfo.limit) * 100)
    : null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <IconWrapper $variant={getVariant()}>
          <i className={`fas ${getIcon()}`} />
        </IconWrapper>
        <Title>{getTitle()}</Title>
        <Description>{getDescription()}</Description>

        {infoType === 'usage_limit_reached' && usagePercent !== null && (
          <UsageBar>
            <UsageBarHeader>
              <span>{upgradeInfo.featureDisplayName || 'Usage'}</span>
              <span>{upgradeInfo.currentUsage} / {upgradeInfo.limit}</span>
            </UsageBarHeader>
            <UsageBarTrack>
              <UsageBarFill $percent={usagePercent} />
            </UsageBarTrack>
            {!isLifetimeSentinel(upgradeInfo.resetDate) && (
              <ResetInfo>
                <i className="fas fa-clock" style={{ fontSize: 10 }} />
                Resets on {formatResetDate(upgradeInfo.resetDate)}
              </ResetInfo>
            )}
          </UsageBar>
        )}

        <TierInfo>
          <TierBadge $type="current">
            {upgradeInfo.currentTier ? formatTierName(upgradeInfo.currentTier) : 'Free'}
          </TierBadge>
          <Arrow className="fas fa-arrow-right" />
          <TierBadge $type="required">
            {formatTierName(upgradeInfo.requiredTier)}
          </TierBadge>
        </TierInfo>
        <ButtonGroup>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
          <button className="btn btn-primary" style={{flex: 1}} onClick={handleViewPlans}>View Plans</button>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
  );
};

export default UpgradeModal;
